import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, ResponsiveTableWrap } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/referrals")({
  component: ReferralsPage,
});


type Coupon = {
  id: string;
  code: string;
  type: "percent" | "fixed";
  value: number;
  max_uses: number | null;
  used_count: number;
  min_order_cents: number;
  expires_at: string | null;
  active: boolean;
};

function ReferralsPage() {
  const { data: coupons = [], isLoading } = useResourceList<Coupon>("coupons");
  const { create, update, remove } = useResourceMutations("coupons");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState<"all" | "active" | "disabled" | "expired">("all");
  const [typeF, setTypeF] = useState<"all" | "percent" | "fixed">("all");
  const empty = (): Partial<Coupon> => ({ code: "", type: "percent", value: 10, max_uses: null, min_order_cents: 0, active: true, expires_at: null });
  const [form, setForm] = useState<Partial<Coupon>>(empty());

  const startNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const startEdit = (c: Coupon) => { setEditing(c); setForm(c); setOpen(true); };
  const duplicate = (c: Coupon) => {
    const { id, used_count, ...rest } = c;
    void id; void used_count;
    create.mutate({ ...rest, code: `${c.code}-COPY`, active: false } as any);
  };
  const copyCode = (code: string) => { navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); };

  const isExpired = (c: Coupon) => c.expires_at && new Date(c.expires_at) < new Date();

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return coupons.filter((c) => {
      if (typeF !== "all" && c.type !== typeF) return false;
      if (statusF === "active" && (!c.active || isExpired(c))) return false;
      if (statusF === "disabled" && c.active) return false;
      if (statusF === "expired" && !isExpired(c)) return false;
      if (!s) return true;
      return c.code.toLowerCase().includes(s);
    });
  }, [coupons, q, statusF, typeF]);

  const save = async () => {
    if (!form.code?.trim()) { toast.error("Code required"); return; }
    const payload = {
      code: form.code.trim().toUpperCase(),
      type: form.type,
      value: form.value,
      max_uses: form.max_uses || null,
      min_order_cents: form.min_order_cents ?? 0,
      expires_at: form.expires_at || null,
      active: form.active ?? true,
    };
    if (editing) await update.mutateAsync({ id: editing.id, values: payload });
    else await create.mutateAsync(payload);
    setOpen(false);
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Referrals & coupons"
        description="Discount codes for checkout."
        onNew={startNew}
        newLabel="New coupon"
        search={q}
        onSearchChange={setQ}
        searchPlaceholder="Search codes…"
        count={coupons.length}
        countLabel="codes"
      />

      <div className="flex flex-wrap gap-1.5">
        {(["all", "active", "disabled", "expired"] as const).map((s) => (
          <button key={s} onClick={() => setStatusF(s)} className={`rounded-full border px-2.5 py-1 text-xs capitalize ${statusF === s ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary"}`}>{s}</button>
        ))}
        <span className="mx-1 self-center text-xs text-muted-foreground">·</span>
        {(["all", "percent", "fixed"] as const).map((t) => (
          <button key={t} onClick={() => setTypeF(t)} className={`rounded-full border px-2.5 py-1 text-xs capitalize ${typeF === t ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary"}`}>{t}</button>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card">
        <ResponsiveTableWrap>
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-xs">
            <tr>
              <th className="px-4 py-2">Code</th>
              <th className="px-4 py-2">Discount</th>
              <th className="hidden sm:table-cell px-4 py-2">Used</th>
              <th className="hidden md:table-cell px-4 py-2">Expires</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-4 py-6 text-muted-foreground">Loading…</td></tr>}
            {!isLoading && filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-muted-foreground">No coupons match.</td></tr>}
            {filtered.map((c) => {
              const expired = isExpired(c);
              return (
                <tr key={c.id} className="border-t border-border">
                  <td className="px-4 py-2">
                    <button onClick={() => copyCode(c.code)} className="inline-flex items-center gap-1.5 font-mono hover:text-primary" title="Copy code">
                      {c.code} <Copy className="h-3 w-3 opacity-60" />
                    </button>
                  </td>
                  <td className="px-4 py-2">{c.type === "percent" ? `${c.value}%` : `$${c.value}`}</td>
                  <td className="hidden sm:table-cell px-4 py-2 text-muted-foreground">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                  <td className="hidden md:table-cell px-4 py-2 text-muted-foreground">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-2">
                    {expired ? <Badge variant="destructive">Expired</Badge> : c.active ? <Badge variant="secondary">Active</Badge> : <Badge variant="outline">Disabled</Badge>}
                  </td>
                  <td className="px-4 py-2"><RowActions onEdit={() => startEdit(c)} onDuplicate={() => duplicate(c)} onDelete={() => { if (confirm("Delete coupon?")) remove.mutate(c.id); }} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </ResponsiveTableWrap>
      </div>


      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit coupon" : "New coupon"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Code</Label>
              <Input value={form.code ?? ""} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as any })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent (%)</SelectItem>
                    <SelectItem value="fixed">Fixed ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Value</Label>
                <Input type="number" value={form.value ?? 0} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Max uses (blank = unlimited)</Label>
                <Input type="number" value={form.max_uses ?? ""} onChange={(e) => setForm({ ...form, max_uses: e.target.value ? Number(e.target.value) : null })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Min order ($)</Label>
                <Input type="number" value={(form.min_order_cents ?? 0) / 100} onChange={(e) => setForm({ ...form, min_order_cents: Math.round(Number(e.target.value) * 100) })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Expires at</Label>
              <Input type="datetime-local" value={form.expires_at ? new Date(form.expires_at).toISOString().slice(0, 16) : ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active ?? true} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-sm">Active</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
